import {GifViewer} from '@wandb/weave/components/Panel2/GifViewer';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import _ from 'lodash';
import * as React from 'react';
import {useState} from 'react';

import {labelComponent, makeCaptions} from '../util/media';
import {useRampFlagVideoStreaming} from '../util/rampFeatureFlags/rampFeatureFlags';
import {InstrumentedCenteredWaveLoader as Loader} from './../components/utility/InstrumentedLoader';
import {type MediaCardProps} from './MediaCard';
import MessageMediaNotFound from './MessageMediaNotFound';
import {SingleRunLink} from './MultiRunWorkspace/SingleRunMode';
import {useGetTrackingCallbacks} from './PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';
import {getModeConfig} from './PanelMediaBrowser/modes/getModeConfig';
import {useVideoSync} from './PanelMediaBrowser/sync/useVideoSync';
import {StreamingVideoViewer} from './VideoStreaming/StreamingVideoViewer';
import {videoMimeTypeFromFilename} from './VideoStreaming/videoMimeTypeFromFilename';

type VideoCardProps = MediaCardProps;

// a single video card with optional synchronization support
const VideoCard = React.memo((props: VideoCardProps) => {
  const {
    run,
    mediaKey,
    mediaIndex,
    runSignature,
    globalStep,
    tileMedia,
    loading,
    customRunName,
    tileMediaSpec,
    getMediaTracking,
    config,
  } = props;
  // While we are waiting for the blob to be downloaded, we need to show a loading state
  // or else the download button will download an empty txt file corresponding to the null blob.
  const isBackgroundDownloadingBlob = tileMedia?.isNullBlob ?? false;
  const objectURL = tileMedia?.objectURL ?? '';
  const filename = tileMedia?.filePath
    ? _.last(tileMedia.filePath.split('/'))
    : undefined;

  // Generate unique video ID for synchronization
  const videoId = `${run.name}-${tileMedia?.step ?? ''}-${mediaKey}-${mediaIndex}`;

  const {videoRefCallback, isSyncEnabled} = useVideoSync({
    videoId,
    isVideoSychronized: config?.videoPlaybackSync ?? false,
  });

  const titleLink = (
    <SingleRunLink
      {...runSignature}
      linkText={customRunName ?? run.displayName}
    />
  );

  // get captions for all images in the group
  const captions = props.omitCaptions
    ? []
    : makeCaptions(tileMedia?.historyRow[mediaKey], mediaIndex);

  const loadingElement = <Loader name="panel-content-loader" size="small" />;

  const {trackSuccess, trackError} = useGetTrackingCallbacks(
    tileMedia,
    tileMediaSpec,
    getMediaTracking
  );

  const isVideoStreamingEnabled = useRampFlagVideoStreaming(run.entityName);
  const hlsSrc = tileMedia?.hlsSrc;
  const mimeTypeInfo = videoMimeTypeFromFilename(filename);

  // In a content-sized layout the tile sizes to its own content, so the viewer
  // pins itself to its decoded dimensions rather than filling the tile. The
  // layout decision lives on the mode config.
  const sizeToIntrinsic = getModeConfig(config?.mode).shouldSizeTilesByContent(
    config ?? {},
    'video'
  );

  const sharedProps = {
    headerElement: labelComponent(props, tileMedia?.step, titleLink),
    footerElement:
      captions.length > 0 ? (
        <div className="image-card-caption">{captions}</div>
      ) : undefined,
    trackError,
    width: props.width,
    height: props.height,
    sizeToIntrinsic,
    refCallback: isSyncEnabled ? videoRefCallback : undefined,
    // videos default to autoplay and loop prior to implementing synchronization
    autoPlay: isSyncEnabled ? undefined : true,
    // need to disable autoloop if sync is enabled
    autoLoop: isSyncEnabled ? undefined : true,
  };

  const [hasFailedToLoadStream, setHasFailedToLoadStream] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const failedLoadElement = (
    <MessageMediaNotFound
      mediaIndex={mediaIndex}
      mediaKey={mediaKey}
      stepIndex={globalStep}
      mediaType="videos"
    />
  );

  const gifOnLoaded = React.useCallback(() => {
    trackSuccess({
      isVideoStreamingEnabledForEntity: isVideoStreamingEnabled,
      wasVideoStreamed: false,
      wasVideoReencodedForStreaming: false,
      totalVideoDuration: 0,
      originalVideoFileSize: tileMedia?.blob.size ?? 0,
    });
  }, [trackSuccess, isVideoStreamingEnabled, tileMedia]);

  const shouldUseHls =
    hlsSrc != null &&
    !hasFailedToLoadStream &&
    isVideoStreamingEnabled &&
    config.disableVideoStream !== true;

  const streamingVideoViewerOnSuccess = React.useCallback(
    (totalVideoDuration: number, initialChunkSize?: number) => {
      setVideoLoaded(true);
      trackSuccess({
        isVideoStreamingEnabledForEntity: isVideoStreamingEnabled,
        wasVideoStreamed: shouldUseHls,
        wasVideoReencodedForStreaming: hlsSrc != null,
        totalVideoDuration,
        originalVideoFileSize: tileMedia?.blob.size ?? 0,
        initialChunkSize,
      });
    },
    [trackSuccess, isVideoStreamingEnabled, tileMedia, hlsSrc, shouldUseHls]
  );

  if (loading) {
    return loadingElement;
  }
  if (tileMedia == null) {
    return failedLoadElement;
  }
  if (mimeTypeInfo.type === 'gif') {
    return (
      <GifViewer
        gifSrc={objectURL}
        gifFilename={filename}
        mediaLoaded={!hasFailedToLoadStream}
        onLoaded={gifOnLoaded}
        onError={trackError}
      />
    );
  }

  const containerSizingClasses = sizeToIntrinsic ? 'h-auto' : 'h-full min-h-0';
  const containerClasses = videoLoaded ? containerSizingClasses : 'hidden';

  return (
    <Tailwind
      className={`px-2 flex flex-col overflow-hidden ${sizeToIntrinsic ? 'w-auto h-auto' : 'w-full h-full min-h-0'}`}>
      {!videoLoaded && loadingElement}
      <div className={containerClasses}>
        <StreamingVideoViewer
          key={config.videoPlayerTimeStep} // force re-render when video player time step changes to re-initialize the video player with the new seek step
          hlsSrc={shouldUseHls ? hlsSrc : null}
          trackSuccess={streamingVideoViewerOnSuccess}
          {...sharedProps}
          setHasFailedToLoadStream={setHasFailedToLoadStream}
          downloadUrl={isBackgroundDownloadingBlob ? null : objectURL}
          mimeType={mimeTypeInfo.mimeType}
          videoPlayerTimeStep={config.videoPlayerTimeStep}
        />
      </div>
    </Tailwind>
  );
});

export default VideoCard;
